import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { apiFetch, ApiRequestError } from "../lib/api.js";
import { setFeedbackContext, clearFeedbackContext } from "../lib/feedbackContext.js";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { useToast } from "../components/Toast.js";
import { MemberPicker, type MemberCandidate } from "../components/MemberPicker.js";
import { ActivityThread } from "../components/ActivityThread.js";
import {
  extractClosureNoteBody,
  formatActivity,
  formatTimestamp,
  MISSING_FIELD_LABEL,
  requiredFieldsFromServed,
  roleLabel,
  sourceKindLabel,
  statusOptionsFromServed,
  type AllowedTransition,
  type ActionItemStatus,
  type ActivityRow,
  type ActionItemCode,
  type ActionItemPriority,
  type ActionItemRow,
  type ActionItemSeverity,
  type Comment,
  type RASCIMember,
  type RASCIRole
} from "../lib/actionItems.js";

/**
 * Action Item detail (ADR-0053 + Phase 4 polish 2026-04-26).
 * Spec: governance-foundation §19.5 + 20260426_111933-action-items-detail-redesign.
 *
 * Route: `/app/action-items/:id`
 *
 * Layout (Phase 4):
 *   - One row of five controls at the top: assignee, status, severity,
 *     priority, due date.
 *   - Metadata strip: Source · Type · Created · (Closed).
 *   - Title (required) + Description (required).
 *   - Conditional code fields appear when the user picks a closing-state
 *     target in the Status dropdown (resolved → resolution + root_cause;
 *     dismissed → dismissal; deferred → deferral_reason + deferred_until).
 *   - Closure notes textarea — editable when transitioning to a closing
 *     state, read-only display otherwise. Required (non-empty after trim)
 *     on closing transitions; validated server-side too.
 *
 * Tabs: Activity (default, merged stream + comment input) and RASCI.
 *
 * Auto-acknowledge: the assignee opening an `open` item triggers a
 * fire-and-forget `mark-seen` POST that promotes status to acknowledged.
 *
 * Activity rendering uses `formatActivity()` to produce sentences — never
 * raw JSON (MEMORY.md: feedback_never_show_json_to_users).
 */

type Tab = "activity" | "rasci";

interface DetailResponse {
  item: ActionItemRow;
  rasci_members: RASCIMember[];
  /** ADR-0154 §8.1 — what this item may legally become, and what each move requires. */
  allowed_transitions: AllowedTransition[];
}

const RASCI_ROLE_ORDER: RASCIRole[] = ["accountable", "responsible", "support", "consulted", "informed"];
const SEVERITIES: ActionItemSeverity[] = ["critical", "high", "medium", "low"];
const PRIORITIES: ActionItemPriority[] = ["critical", "high", "medium", "low"];

/**
 * Editable form. Includes core item fields PLUS the status dropdown +
 * closing-state fields (codes, deferred-until date, closure_notes). The
 * closure fields are submitted only as part of a closing transition; if
 * the user types in the closure_notes textarea without changing status,
 * the typed value is held in local state but not persisted.
 */
interface EditForm {
  title: string;
  description: string;
  severity: ActionItemSeverity;
  priority: ActionItemPriority;
  due_at: string; // yyyy-mm-dd or ""
  assignee_user_id: string | null;
  status: ActionItemStatus;
  // Conditional fields (only relevant when transitioning to the matching status)
  resolution_code_id: string;
  root_cause_code_id: string;
  dismissal_code_id: string;
  deferral_reason_code_id: string;
  deferred_until: string; // yyyy-mm-dd or ""
  closure_notes: string;
}

/**
 * Build the PATCH body for the non-status fields. Returns only the keys
 * that differ from the initially-loaded item — empty object means no
 * PATCH is needed.
 *
 * `closure_notes` only flows through PATCH when the status itself isn't
 * changing — when it IS changing, the value is sent in the /transition
 * body instead, so the activity log carries `status_change` (not
 * `closure_notes_edited`) for the same write.
 */
function patchBodyFor(form: EditForm, initial: EditForm): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (form.title !== initial.title) out.title = form.title.trim();
  if (form.description !== initial.description) out.description = form.description.trim() || null;
  if (form.severity !== initial.severity) out.severity = form.severity;
  if (form.priority !== initial.priority) out.priority = form.priority;
  if (form.due_at !== initial.due_at) {
    out.due_at = form.due_at ? new Date(`${form.due_at}T00:00:00`).toISOString() : null;
  }
  if (form.assignee_user_id !== initial.assignee_user_id) out.assignee_user_id = form.assignee_user_id;
  if (form.status === initial.status && form.closure_notes.trim() !== (initial.closure_notes ?? "").trim()) {
    out.closure_notes = form.closure_notes.trim();
  }
  return out;
}

function itemToForm(item: ActionItemRow): EditForm {
  return {
    title: item.title,
    description: item.description ?? "",
    severity: item.severity,
    priority: item.priority,
    due_at: item.due_at ? item.due_at.slice(0, 10) : "",
    assignee_user_id: item.assignee_user_id,
    status: item.status,
    // Closure-related state seeded from the persisted item so the dropdowns
    // and textarea show whatever's on file (even on closed items where
    // they're read-only).
    resolution_code_id: item.resolution_code_id ?? "",
    root_cause_code_id: item.root_cause_code_id ?? "",
    dismissal_code_id: item.dismissal_code_id ?? "",
    deferral_reason_code_id: item.deferral_reason_code_id ?? "",
    deferred_until: item.deferred_until ? item.deferred_until.slice(0, 10) : "",
    closure_notes: item.closure_notes ?? ""
  };
}

const CLOSING_STATUSES: ReadonlySet<ActionItemStatus> = new Set(["deferred", "resolved", "dismissed"]);
function isClosing(s: ActionItemStatus): boolean {
  return CLOSING_STATUSES.has(s);
}

export function ActionItemDetail() {
  const { id } = useParams();
  const toast = useToast();

  const [item, setItem] = useState<ActionItemRow | null>(null);
  // ADR-0154 §6.3 C1 — the state machine is the server's. The console renders
  // what it is told is legal and keeps only the labels.
  const [allowedTransitions, setAllowedTransitions] = useState<AllowedTransition[]>([]);

  // ADR-0074: register feedback context so the popover names this action item.
  useEffect(() => {
    if (!id) return;
    setFeedbackContext({ kind: "action_item", id, displayName: item?.title ?? null });
    return () => clearFeedbackContext({ kind: "action_item", id });
  }, [id, item?.title]);

  const [members, setMembers] = useState<RASCIMember[]>([]);
  const [activity, setActivity] = useState<ActivityRow[]>([]);
  const [comments, setComments] = useState<Comment[]>([]);
  const [directory, setDirectory] = useState<Map<string, MemberCandidate>>(new Map());
  const [candidatesList, setCandidatesList] = useState<MemberCandidate[]>([]);
  const [itemTypes, setItemTypes] = useState<ActionItemCode[]>([]);
  const [deferralReasons, setDeferralReasons] = useState<ActionItemCode[]>([]);
  const [resolutions, setResolutions] = useState<ActionItemCode[]>([]);
  const [rootCauses, setRootCauses] = useState<ActionItemCode[]>([]);
  const [dismissals, setDismissals] = useState<ActionItemCode[]>([]);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [currentUserEntitlement, setCurrentUserEntitlement] = useState<string | null>(null);
  // ADR-0221 §3.6: the issue this item was created from, when source_ref carries one — its title read once.
  const [fromIssue, setFromIssue] = useState<{ id: string; title: string } | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("activity");

  const [editForm, setEditForm] = useState<EditForm | null>(null);
  const [saveBusy, setSaveBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  /**
   * Per-field validation errors keyed by the same string used in the hub's
   * `missing_fields` array (`closure_notes`, `resolution_code_id`, etc.) so
   * client-side and server-side validation share one error map.
   */
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const [detail, act, cmt, dir, types, defs, res, roots, dism, me] = await Promise.all([
        apiFetch<DetailResponse>(`/api/tenant/action-items/${id}`),
        apiFetch<{ activity: ActivityRow[] }>(`/api/tenant/action-items/${id}/activity?limit=100`),
        apiFetch<{ comments: Comment[] }>(`/api/tenant/action-items/${id}/comments?limit=100`),
        apiFetch<{ candidates: MemberCandidate[] }>("/api/tenant/rasci-candidates").catch(() => ({ candidates: [] as MemberCandidate[] })),
        apiFetch<{ codes: ActionItemCode[] }>("/api/tenant/action-item-codes/item-types").catch(() => ({ codes: [] as ActionItemCode[] })),
        apiFetch<{ codes: ActionItemCode[] }>("/api/tenant/action-item-codes/deferral-reasons").catch(() => ({ codes: [] as ActionItemCode[] })),
        apiFetch<{ codes: ActionItemCode[] }>("/api/tenant/action-item-codes/resolution-codes").catch(() => ({ codes: [] as ActionItemCode[] })),
        apiFetch<{ codes: ActionItemCode[] }>("/api/tenant/action-item-codes/root-cause-codes").catch(() => ({ codes: [] as ActionItemCode[] })),
        apiFetch<{ codes: ActionItemCode[] }>("/api/tenant/action-item-codes/dismissal-codes").catch(() => ({ codes: [] as ActionItemCode[] })),
        apiFetch<{ user: { id: string; entitlement?: string } }>("/api/tenant/me").catch(() => null)
      ]);
      setItem(detail.item);
      setAllowedTransitions(detail.allowed_transitions ?? []);
      setEditForm(itemToForm(detail.item));
      const issueId = typeof (detail.item.source_ref as Record<string, unknown> | undefined)?.initiative_issue_id === "string" ? String((detail.item.source_ref as Record<string, unknown>).initiative_issue_id) : null;
      if (issueId) {
        void apiFetch<{ issue: { id: string; title: string } }>(`/api/tenant/issues/${encodeURIComponent(issueId)}`)
          .then((r) => setFromIssue({ id: r.issue.id, title: r.issue.title }))
          .catch(() => setFromIssue({ id: issueId, title: "an issue" }));
      } else setFromIssue(null);
      setMembers(detail.rasci_members);
      setActivity(act.activity);
      setComments(cmt.comments);
      setCandidatesList(dir.candidates);
      const m = new Map<string, MemberCandidate>();
      for (const c of dir.candidates) m.set(c.id, c);
      setDirectory(m);
      setItemTypes(types.codes);
      setDeferralReasons(defs.codes);
      setResolutions(res.codes);
      setRootCauses(roots.codes);
      setDismissals(dism.codes);
      if (me) {
        setCurrentUserId(me.user.id);
        setCurrentUserEntitlement(me.user.entitlement ?? null);
      }
    } catch (e) {
      setError(e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Auto-acknowledge on assignee view (Phase 4 polish §3.4).
   * Fire-and-forget POST. Hub no-ops if caller != assignee or status != open.
   * On a successful change, refresh the item + activity in the local view.
   */
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await apiFetch<{ changed: boolean; item: ActionItemRow; allowed_transitions?: AllowedTransition[] }>(
          `/api/tenant/action-items/${id}/mark-seen`,
          { method: "POST" }
        );
        if (cancelled || !res.changed) return;
        setItem(res.item);
        setAllowedTransitions(res.allowed_transitions ?? []);
        setEditForm(itemToForm(res.item));
        // Refresh activity so the new status_change event shows up.
        const act = await apiFetch<{ activity: ActivityRow[] }>(
          `/api/tenant/action-items/${id}/activity?limit=100`
        );
        if (!cancelled) setActivity(act.activity);
      } catch {
        // Silent — auto-ack is best-effort. Surfacing an error here would
        // flash a toast every time a non-assignee opens the page.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const initialForm = useMemo(() => (item ? itemToForm(item) : null), [item]);

  function memberLabel(accountId: string | null): string {
    if (!accountId) return "—";
    const c = directory.get(accountId);
    if (!c) return `${accountId.slice(0, 8)}…`;
    const tag = c.kind === "contact" ? " (contact)" : c.kind === "group" ? " (group)" : "";
    return `${c.display_name}${tag}`;
  }

  function codeLabel(codes: ActionItemCode[], codeId: string | null): string {
    if (!codeId) return "—";
    return codes.find((c) => c.id === codeId)?.label ?? `${codeId.slice(0, 8)}…`;
  }

  const itemTypeLabel = (itemTypeId: string) =>
    itemTypes.find((c) => c.id === itemTypeId)?.label ?? `${itemTypeId.slice(0, 8)}…`;

  // ---- Save model --------------------------------------------------------
  //
  // This page uses an explicit Save button rather than autosave. The reason
  // is the closing-transition (deferred / resolved / dismissed) write
  // shape: codes + deferred_until + closure_notes are write-once via
  // `/transition`. Once the transition fires, those fields are no longer
  // editable on the server side without re-opening, so partial autosaves
  // would either land a half-finished transition or silently drop later
  // edits to the closure fields. A single explicit commit avoids both.
  //
  // The Save button is enabled only when the form differs from the last
  // saved snapshot AND validation passes. Cancel reverts to the saved
  // state.
  // ------------------------------------------------------------------------

  const saveInFlightRef = useRef(false);
  const itemRef = useRef<ActionItemRow | null>(null);
  const editFormRef = useRef<EditForm | null>(null);
  const initialFormRef = useRef<EditForm | null>(null);
  itemRef.current = item;
  editFormRef.current = editForm;
  initialFormRef.current = initialForm;

  /** ISO yyyy-mm-dd for "today" — used as `min` on `deferred_until`. */
  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);

  /** Compute the current set of missing-field errors for a status transition.
   *  Returns `{}` when there's no transition, or when all required fields
   *  are filled. */
  function missingFieldsFor(form: EditForm, prev: EditForm): Record<string, string> {
    if (form.status === prev.status) return {};
    const reqs = requiredFieldsFromServed(allowedTransitions, form.status);
    const errs: Record<string, string> = {};
    if (reqs.deferral_reason && !form.deferral_reason_code_id) {
      errs.deferral_reason_code_id = `${MISSING_FIELD_LABEL.deferral_reason_code_id} is required.`;
    }
    if (reqs.deferred_until && !form.deferred_until) {
      errs.deferred_until = `${MISSING_FIELD_LABEL.deferred_until} is required.`;
    }
    if (reqs.resolution && !form.resolution_code_id) {
      errs.resolution_code_id = `${MISSING_FIELD_LABEL.resolution_code_id} is required.`;
    }
    if (reqs.root_cause && !form.root_cause_code_id) {
      errs.root_cause_code_id = `${MISSING_FIELD_LABEL.root_cause_code_id} is required.`;
    }
    if (reqs.dismissal && !form.dismissal_code_id) {
      errs.dismissal_code_id = `${MISSING_FIELD_LABEL.dismissal_code_id} is required.`;
    }
    if (reqs.closure_notes && !form.closure_notes.trim()) {
      errs.closure_notes = `${MISSING_FIELD_LABEL.closure_notes} is required.`;
    }
    return errs;
  }

  /**
   * Validate the form for save. Returns a map of fieldId → error message;
   * empty when the form is valid. Includes:
   *   - Title / Description required (never empty after trim)
   *   - Status transition required-fields when status changed to a
   *     closing target (codes + closure_notes)
   *   - deferred_until must not be in the past
   */
  function validateForSave(form: EditForm, prev: EditForm): Record<string, string> {
    const errs: Record<string, string> = {};
    if (!form.title.trim()) errs.title = "Title is required.";
    if (!form.description.trim()) errs.description = "Description is required.";

    const statusChanged = form.status !== prev.status;
    if (statusChanged) {
      Object.assign(errs, missingFieldsFor(form, prev));
    }

    // deferred_until: must be today or later if it's set + relevant.
    if (form.status === "deferred" && form.deferred_until && form.deferred_until < todayIso) {
      errs.deferred_until = "Deferred until cannot be in the past.";
    }

    return errs;
  }

  /** Save handler — fires PATCH for dirty non-status fields, /transition
   *  for a status change. Both run in parallel. Validation runs first;
   *  invalid → show inline errors, don't fire. */
  async function saveAll() {
    const item = itemRef.current;
    const form = editFormRef.current;
    const prev = initialFormRef.current;
    if (!item || !form || !prev) return;
    if (saveInFlightRef.current) return;

    const errs = validateForSave(form, prev);
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      setSaveError("Please fix the highlighted fields.");
      return;
    }

    const patchBody = patchBodyFor(form, prev);
    const needsPatch = Object.keys(patchBody).length > 0;
    const statusChanged = form.status !== prev.status;
    if (!needsPatch && !statusChanged) return; // nothing to do

    let transitionBody: Record<string, unknown> | null = null;
    if (statusChanged) {
      const reqs = requiredFieldsFromServed(allowedTransitions, form.status);
      transitionBody = { to_status: form.status };
      if (reqs.deferral_reason) transitionBody.deferral_reason_code_id = form.deferral_reason_code_id;
      if (reqs.deferred_until) {
        transitionBody.deferred_until = new Date(`${form.deferred_until}T00:00:00`).toISOString();
      }
      if (reqs.resolution) transitionBody.resolution_code_id = form.resolution_code_id;
      if (reqs.root_cause) transitionBody.root_cause_code_id = form.root_cause_code_id;
      if (reqs.dismissal) transitionBody.dismissal_code_id = form.dismissal_code_id;
      if (reqs.closure_notes) transitionBody.closure_notes = form.closure_notes.trim();
    }

    saveInFlightRef.current = true;
    setSaveBusy(true);
    setSaveError(null);
    setFieldErrors({});
    try {
      const calls: Array<Promise<{ item: ActionItemRow; allowed_transitions?: AllowedTransition[] }>> = [];
      if (needsPatch) {
        calls.push(
          apiFetch<{ item: ActionItemRow; allowed_transitions?: AllowedTransition[] }>(`/api/tenant/action-items/${item.id}`, {
            method: "PATCH",
            body: patchBody
          })
        );
      }
      if (transitionBody) {
        calls.push(
          apiFetch<{ item: ActionItemRow; allowed_transitions?: AllowedTransition[] }>(
            `/api/tenant/action-items/${item.id}/transition`,
            { method: "POST", body: transitionBody }
          )
        );
      }
      const results = await Promise.all(calls);
      const updated = results[results.length - 1].item;
      setItem(updated);
      // The legal moves change with the status; take the server's fresh answer
      // rather than leaving the dropdown describing the previous state.
      setAllowedTransitions(results[results.length - 1].allowed_transitions ?? []);
      setEditForm(itemToForm(updated));
      // Every save writes at least one activity row (/transition writes
      // status_change; PATCH writes one row per changed field), so always
      // refetch — the timeline must show the change without a page reload.
      const act = await apiFetch<{ activity: ActivityRow[] }>(
        `/api/tenant/action-items/${item.id}/activity?limit=100`
      );
      setActivity(act.activity);
      toast.push({ kind: "success", message: "Saved." });
    } catch (e) {
      // missing_fields → inline validation; otherwise surface as toast.
      const serverFields =
        e instanceof ApiRequestError && Array.isArray(e.data?.missing_fields)
          ? (e.data.missing_fields as string[])
          : null;
      if (serverFields) {
        const serverErrs: Record<string, string> = {};
        for (const f of serverFields) {
          serverErrs[f] = `${MISSING_FIELD_LABEL[f] ?? f} is required.`;
        }
        setFieldErrors(serverErrs);
        setSaveError("Please fix the highlighted fields.");
      } else {
        const msg = e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e);
        setSaveError(msg);
        toast.push({ kind: "error", message: msg });
      }
    } finally {
      saveInFlightRef.current = false;
      setSaveBusy(false);
    }
  }

  /** Discard pending edits and restore the last saved snapshot. */
  function cancelEdits() {
    if (item) setEditForm(itemToForm(item));
    setFieldErrors({});
    setSaveError(null);
  }

  async function addComment(body: string) {
    if (!item) return;
    await apiFetch(`/api/tenant/action-items/${item.id}/comments`, {
      method: "POST",
      body: { body }
    });
    const [cmt, act] = await Promise.all([
      apiFetch<{ comments: Comment[] }>(`/api/tenant/action-items/${item.id}/comments?limit=100`),
      apiFetch<{ activity: ActivityRow[] }>(`/api/tenant/action-items/${item.id}/activity?limit=100`)
    ]);
    setComments(cmt.comments);
    setActivity(act.activity);
  }

  async function editCommentBody(commentId: string, body: string) {
    if (!item) return;
    await apiFetch(`/api/tenant/action-items/${item.id}/comments/${commentId}`, {
      method: "PATCH",
      body: { body }
    });
    const cmt = await apiFetch<{ comments: Comment[] }>(`/api/tenant/action-items/${item.id}/comments?limit=100`);
    setComments(cmt.comments);
  }

  return (
    <PageLayout title={item?.title ?? "Action item"}>
      <div style={{ marginBottom: 12 }}>
        <Link to="/app/action-items" className="muted" style={{ fontSize: 13 }}>← Back to Action Items</Link>
      </div>

      {error && <ErrorState detail={error} />}
      {loading && !error && <LoadingState />}

      {!loading && !error && item && editForm && (() => {
        const statusOptions = statusOptionsFromServed(initialForm?.status ?? item.status, allowedTransitions);
        const transitioning = !!initialForm && editForm.status !== initialForm.status;
        const reqs = transitioning ? requiredFieldsFromServed(allowedTransitions, editForm.status) : null;
        const targetIsClosing = transitioning && isClosing(editForm.status);
        // The current item is closed (resolved/dismissed) → status select is
        // disabled (no transitions allowed from a terminal).
        const itemTerminal = item.status === "resolved" || item.status === "dismissed";
        // Closure notes editable when:
        //   1. The user is picking a closing target (write-via-transition), OR
        //   2. The item is already in a closing state (deferred/resolved/
        //      dismissed) — post-closure edit-via-PATCH. Authorization is
        //      enforced server-side; the textarea stays editable in the UI.
        const notesEditable = targetIsClosing || isClosing(item.status);

        function patch(updates: Partial<EditForm>) {
          setEditForm((f) => (f ? { ...f, ...updates } : f));
        }

        const userCandidates = candidatesList.filter((c) => c.kind === "user");
        const hasPendingChanges = !!initialForm && JSON.stringify(editForm) !== JSON.stringify(initialForm);

        return (
        <div style={{ display: "grid", gap: 12 }}>
          {/* Source + Type pills — page-level metadata, very top */}
          <div className="aiDetailMetaPills">
            <span className="pill"><strong>Source</strong> {sourceKindLabel(item.source_kind)}</span>
            <span className="pill"><strong>Type</strong> {itemTypeLabel(item.item_type_code_id)}</span>
            {fromIssue && (
              <span className="pill"><strong>From issue</strong> <Link to={`/app/issues/${fromIssue.id}`}>{fromIssue.title}</Link></span>
            )}
          </div>

          {/* Created / Closed metadata — sits between the pills and the card */}
          <div className="aiDetailMetaTimes">
            Created {formatTimestamp(item.created_at)}
            {item.closed_at && <> · Closed {formatTimestamp(item.closed_at)}</>}
          </div>

          {/* Editable top card */}
          <div className="card" style={{ display: "grid", gap: 12 }}>
            {/* Title */}
            <label className="field">
              <span className="label">Title *</span>
              <input
                className="input"
                value={editForm.title}
                onChange={(e) => patch({ title: e.target.value })}
              />
              {fieldErrors.title && <div className="help" style={{ color: "var(--danger-text)" }}>{fieldErrors.title}</div>}
            </label>

            {/* Description */}
            <label className="field">
              <span className="label">Description *</span>
              <textarea
                className="input"
                rows={5}
                value={editForm.description}
                onChange={(e) => patch({ description: e.target.value })}
                placeholder="What's wrong, what needs to happen, and any context"
              />
              {fieldErrors.description && (
                <div className="help" style={{ color: "var(--danger-text)" }}>{fieldErrors.description}</div>
              )}
            </label>

            {/* Five-control row — same shape, same height */}
            <div className="aiDetailControlRow">
              <label className="field">
                <span className="label">Assignee</span>
                <select
                  className="select"
                  value={editForm.assignee_user_id ?? ""}
                  onChange={(e) => patch({ assignee_user_id: e.target.value || null })}
                >
                  <option value="">Unassigned</option>
                  {userCandidates.map((c) => (
                    <option key={c.id} value={c.id}>{c.display_name}</option>
                  ))}
                </select>
              </label>

              <label className="field">
                <span className="label">Status</span>
                <select
                  className="select"
                  value={editForm.status}
                  disabled={itemTerminal}
                  onChange={(e) => patch({ status: e.target.value as ActionItemStatus })}
                >
                  {statusOptions.map((opt) => (
                    <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>

              <label className="field">
                <span className="label">Severity</span>
                <select
                  className="select"
                  value={editForm.severity}
                  onChange={(e) => patch({ severity: e.target.value as ActionItemSeverity })}
                >
                  {SEVERITIES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </label>

              <label className="field">
                <span className="label">Priority</span>
                <select
                  className="select"
                  value={editForm.priority}
                  onChange={(e) => patch({ priority: e.target.value as ActionItemPriority })}
                >
                  {PRIORITIES.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </label>

              <label className="field">
                <span className="label">Due date</span>
                <input
                  type="date"
                  className="input"
                  value={editForm.due_at}
                  onChange={(e) => patch({ due_at: e.target.value })}
                />
              </label>
            </div>

            {/*
             * Conditional code fields — visible whenever the displayed
             * status is a closing state (acceptance criterion §8.11). Inputs
             * are editable while the user is transitioning to that status,
             * disabled otherwise (e.g. on an already-resolved item).
             */}
            {editForm.status === "resolved" && (
              <div className="aiDetailCodesRow">
                <label className="field">
                  <span className="label">Resolution code{transitioning ? " *" : ""}</span>
                  <select
                    className="select"
                    value={editForm.resolution_code_id}
                    onChange={(e) => patch({ resolution_code_id: e.target.value })}
                    disabled={!transitioning}
                  >
                    <option value="">Select…</option>
                    {resolutions.map((c) => (
                      <option key={c.id} value={c.id}>{c.label}</option>
                    ))}
                  </select>
                  <div className="fieldErrorSlot">{fieldErrors.resolution_code_id ?? ""}</div>
                </label>
                <label className="field">
                  <span className="label">Root cause code{transitioning ? " *" : ""}</span>
                  <select
                    className="select"
                    value={editForm.root_cause_code_id}
                    onChange={(e) => patch({ root_cause_code_id: e.target.value })}
                    disabled={!transitioning}
                  >
                    <option value="">Select…</option>
                    {rootCauses.map((c) => (
                      <option key={c.id} value={c.id}>{c.label}</option>
                    ))}
                  </select>
                  <div className="fieldErrorSlot">{fieldErrors.root_cause_code_id ?? ""}</div>
                </label>
              </div>
            )}

            {editForm.status === "dismissed" && (
              <div className="aiDetailCodesRow">
                <label className="field">
                  <span className="label">Dismissal code{transitioning ? " *" : ""}</span>
                  <select
                    className="select"
                    value={editForm.dismissal_code_id}
                    onChange={(e) => patch({ dismissal_code_id: e.target.value })}
                    disabled={!transitioning}
                  >
                    <option value="">Select…</option>
                    {dismissals.map((c) => (
                      <option key={c.id} value={c.id}>{c.label}</option>
                    ))}
                  </select>
                  <div className="fieldErrorSlot">{fieldErrors.dismissal_code_id ?? ""}</div>
                </label>
              </div>
            )}

            {editForm.status === "deferred" && (
              <div className="aiDetailCodesRow">
                <label className="field">
                  <span className="label">Deferral reason{transitioning ? " *" : ""}</span>
                  <select
                    className="select"
                    value={editForm.deferral_reason_code_id}
                    onChange={(e) => patch({ deferral_reason_code_id: e.target.value })}
                    disabled={!transitioning}
                  >
                    <option value="">Select…</option>
                    {deferralReasons.map((c) => (
                      <option key={c.id} value={c.id}>{c.label}</option>
                    ))}
                  </select>
                  <div className="fieldErrorSlot">{fieldErrors.deferral_reason_code_id ?? ""}</div>
                </label>
                <label className="field">
                  <span className="label">Deferred until{transitioning ? " *" : ""}</span>
                  <input
                    type="date"
                    className="input"
                    value={editForm.deferred_until}
                    min={todayIso}
                    onChange={(e) => patch({ deferred_until: e.target.value })}
                    disabled={!transitioning}
                  />
                  <div className="fieldErrorSlot">{fieldErrors.deferred_until ?? ""}</div>
                </label>
              </div>
            )}

            {/* Closure notes — always visible. Editable only while picking a closing target. */}
            <label className="field">
              <span className="label">
                Closure notes{notesEditable ? " *" : ""}
              </span>
              {notesEditable ? (
                <textarea
                  className="input"
                  rows={3}
                  value={editForm.closure_notes}
                  onChange={(e) => patch({ closure_notes: e.target.value })}
                  placeholder="Why is this being deferred / resolved / dismissed?"
                />
              ) : (
                <textarea
                  className="input"
                  rows={3}
                  value={editForm.closure_notes}
                  readOnly
                  placeholder="—"
                />
              )}
              {fieldErrors.closure_notes && (
                <div className="help" style={{ color: "var(--danger-text)" }}>{fieldErrors.closure_notes}</div>
              )}
              {!notesEditable && !itemTerminal && !item.closure_notes && (
                <div className="help">
                  Saved when you transition to deferred, resolved, or dismissed.
                </div>
              )}
            </label>

            {/* Top-level non-field error (e.g. server message). Field-level
                errors render inline next to their controls; this is the
                catch-all. */}
            {saveError && (
              <div className="help" style={{ color: "var(--danger-text)" }}>{saveError}</div>
            )}

            {/* Footer: Save + Cancel, right-aligned. */}
            <div className="aiDetailFooter" style={{ justifyContent: "flex-end" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <button
                  type="button"
                  className="btn btnSm"
                  disabled={!hasPendingChanges || saveBusy}
                  onClick={cancelEdits}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btnSm btnPrimary"
                  disabled={!hasPendingChanges || saveBusy}
                  onClick={() => void saveAll()}
                >
                  {saveBusy ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="tabs">
            <button type="button" className={`tab${tab === "activity" ? " tabActive" : ""}`} onClick={() => setTab("activity")}>
              Activity
            </button>
            <button type="button" className={`tab${tab === "rasci" ? " tabActive" : ""}`} onClick={() => setTab("rasci")}>
              RASCI
            </button>
          </div>

          {tab === "activity" && (
            <ActivityThread
              comments={comments}
              activity={activity}
              currentUserId={currentUserId}
              formatRow={(a) => ({
                actor: a.actor_user_id ? memberLabel(a.actor_user_id) : "System",
                text: formatActivity(a.event_type, a.payload, memberLabel),
                note: extractClosureNoteBody(a.event_type, a.payload),
              })}
              commentAuthor={(c) => ({ id: c.author_user_id, label: memberLabel(c.author_user_id) })}
              onAdd={addComment}
              onEdit={editCommentBody}
            />
          )}

          {tab === "rasci" && (
            <RasciTab
              item={item}
              members={members}
              memberLabel={memberLabel}
              currentUserId={currentUserId}
              currentUserEntitlement={currentUserEntitlement}
              candidates={candidatesList}
              onRasciUpdated={load}
            />
          )}
        </div>
        );
      })()}
    </PageLayout>
  );
}

// ---------------------------------------------------------------------------
// Overview tab — RASCI + closure summary + unified timeline
// ---------------------------------------------------------------------------

type EditableRole = "responsible" | "support" | "consulted" | "informed";
const EDITABLE_ROLES: EditableRole[] = ["responsible", "support", "consulted", "informed"];

function splitByCandidateKind(
  ids: string[],
  candidates: MemberCandidate[]
): { user_ids: string[]; group_ids: string[] } {
  const byId = new Map(candidates.map((c) => [c.id, c]));
  const user_ids: string[] = [];
  const group_ids: string[] = [];
  for (const id of ids) {
    const c = byId.get(id);
    if (!c) continue;
    if (c.kind === "group") group_ids.push(id);
    else user_ids.push(id); // user or contact
  }
  return { user_ids, group_ids };
}

// ---------------------------------------------------------------------------
// Activity tab — merged comment thread + system events
//
// Phase 4 polish §3.5. Single chronological stream rendered newest-first
// with the comment input pinned to the top. System activity events render
// as compact one-line rows; comments render with their full body. The
// `comment` event_type is filtered out of the activity stream because the
// comment object itself is in the merged stream — including the event
// would duplicate the row.
// ---------------------------------------------------------------------------

function RasciTab(props: {
  item: ActionItemRow;
  members: RASCIMember[];
  memberLabel: (id: string | null) => string;
  currentUserId: string | null;
  currentUserEntitlement: string | null;
  candidates: MemberCandidate[];
  onRasciUpdated: () => void | Promise<void>;
}) {
  const { item, members, memberLabel } = props;
  const canEditRasci =
    props.currentUserId === item.assignee_user_id || props.currentUserEntitlement === "admin";

  const toast = useToast();
  const [rasciEditing, setRasciEditing] = useState(false);
  const [savingRasci, setSavingRasci] = useState(false);
  const [drafts, setDrafts] = useState<Record<EditableRole, string[]>>({
    responsible: [],
    support: [],
    consulted: [],
    informed: []
  });

  function openEdit() {
    const next: Record<EditableRole, string[]> = {
      responsible: [],
      support: [],
      consulted: [],
      informed: []
    };
    for (const m of members) {
      if (m.role === "accountable") continue; // A lives on assignee_user_id (migration 111)
      if (EDITABLE_ROLES.includes(m.role as EditableRole)) {
        next[m.role as EditableRole].push(m.account_id);
      }
    }
    setDrafts(next);
    setRasciEditing(true);
  }
  function cancelEdit() {
    setRasciEditing(false);
  }
  async function saveEdit() {
    setSavingRasci(true);
    try {
      const body: Record<string, { user_ids: string[]; group_ids: string[] }> = {};
      for (const role of EDITABLE_ROLES) {
        body[role] = splitByCandidateKind(drafts[role], props.candidates);
      }
      await apiFetch(`/api/tenant/action-items/${item.id}/rasci`, { method: "PATCH", body });
      toast.push({ kind: "success", message: "RASCI updated." });
      setRasciEditing(false);
      await props.onRasciUpdated();
    } catch (e) {
      const msg = e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e);
      toast.push({ kind: "error", message: msg });
    } finally {
      setSavingRasci(false);
    }
  }

  const accountableId = item.assignee_user_id;

  return (
    <div className="card">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <div style={{ fontWeight: 700 }}>RASCI</div>
        {canEditRasci && !rasciEditing && (
          <button type="button" className="btn" onClick={openEdit} style={{ fontSize: 12, padding: "4px 10px" }}>
            Edit RASCI
          </button>
        )}
      </div>

      {!rasciEditing ? (
        <div style={{ display: "grid", gap: 6 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
            <span className="label" style={{ width: 110 }}>{roleLabel("accountable")}</span>
            <span>
              {accountableId ? memberLabel(accountableId) : <span className="muted">— not set —</span>}
            </span>
          </div>
          {EDITABLE_ROLES.map((role) => {
            const list = members.filter((m) => m.role === role);
            if (list.length === 0) {
              return (
                <div key={role} style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
                  <span className="label" style={{ width: 110 }}>{roleLabel(role)}</span>
                  <span className="muted">— none —</span>
                </div>
              );
            }
            return (
              <div key={role} style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
                <span className="label" style={{ width: 110 }}>{roleLabel(role)}</span>
                <span>{list.map((m) => memberLabel(m.account_id)).join(", ")}</span>
              </div>
            );
          })}
          <div className="help" style={{ marginTop: 6 }}>
            Accountable is the Assignee — change it in the card at the top of the page.
          </div>
        </div>
      ) : (
        <div style={{ display: "grid", gap: 10 }}>
          <div className="help" style={{ marginTop: 0 }}>
            Accountable is the Assignee (edit in the top card). Edit R / S / C / I here.
          </div>
          {EDITABLE_ROLES.map((role) => (
            <div key={role} className="field">
              <span className="label">{roleLabel(role)}</span>
              <MemberPicker
                mode="multi"
                allowedKinds={["user", "contact", "group"]}
                candidates={props.candidates}
                selected={drafts[role]}
                onChange={(next) => setDrafts((d) => ({ ...d, [role]: next }))}
                placeholder={`Add ${roleLabel(role).toLowerCase()}…`}
              />
            </div>
          ))}
          <div className="actions" style={{ justifyContent: "flex-end" }}>
            <button type="button" className="btn" onClick={cancelEdit} disabled={savingRasci}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btnPrimary"
              onClick={() => void saveEdit()}
              disabled={savingRasci}
            >
              {savingRasci ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export type { RASCIRole };
